import React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Box } from "@salestrak/ui";
import { PageHeader, PageHeaderTitle, SETTINGS_CONTENT_MAX_WIDTH, SettingsPageGutter } from "#app/components/page-header";
import { useUserSettings } from "#app/lib/hooks/use-user-settings";
import { LoadErrorState } from "#app/components/settings/load-error-state";
import { SettingsGroup, SettingsGroupContent, SettingsGroupRow } from "#app/components/settings/settings-group";
import { APPEARANCE_ANCHORS } from "#app/components/settings-search/rows/appearance";
import { SettingsHighlightTarget } from "#app/components/settings-search/settings-highlight";
import {
  ThemeAppearanceCards,
  ThemeModeSection,
  ThemeResetButton,
} from "#app/components/theme/theme-appearance-controls";
import {
  ThemeEditingMode,
  useIsAppearanceCustomized,
  useThemeAppearanceMode,
} from "#app/components/theme/use-theme-appearance-state";
import { SettingsPageSkeleton } from "#app/components/settings/settings-page-skeleton";

function AppearancePage() {
  // The theme stack reads and writes the same preferences bag as the
  // Preferences page, so loading and error gate on the same query.
  const { preferences, isLoading, error, refetch } = useUserSettings();
  const isAppearanceCustomized = useIsAppearanceCustomized();
  const appearanceMode = useThemeAppearanceMode();

  if (isLoading && !preferences) {
    return (
      <Box fill="flex" overflow="auto">
        <PageHeader centered>
          <PageHeaderTitle>Appearance</PageHeaderTitle>
        </PageHeader>
        {/* Mirrors the settled run: the theme cards (approximated at 5
          * unlabeled rows), then one mode section (header + the colour
          * rows) — so the resolve lands with no reflow above the fold. */}
        <SettingsPageSkeleton
          groups={[
            { rows: 5, unlabeled: true },
            { rows: 5 },
          ]}
        />
      </Box>
    );
  }

  if (error && !preferences) {
    return <LoadErrorState noun="appearance settings" error={error} onRetry={() => { void refetch(); }} />;
  }

  return (
    <Box fill="flex" overflow="auto">
      <PageHeader centered>
        <PageHeaderTitle>Appearance</PageHeaderTitle>
      </PageHeader>
      <Box direction="column" align="center" paddingY="md" paddingX="md">
        <Box direction="column" gap="base" fill="width" maxWidth={SETTINGS_CONTENT_MAX_WIDTH}>
          {/* One decision per MODE, the way the reference lays it out: pick
              which appearance is in use, then each mode gets its own theme
              and its own colours. System shows BOTH, because both are. */}
          <SettingsHighlightTarget anchor={APPEARANCE_ANCHORS.theme}>
            <SettingsGroup>
              {/* In a card: bare on the page the three cards read as loose
                  art rather than as the control they are, and they break the
                  rhythm every other group here keeps. */}
              <SettingsGroupRow
                header
                label="Theme"
                subtitle="Which appearance the interface uses"
              />
              <SettingsGroupContent>
                <ThemeAppearanceCards />
              </SettingsGroupContent>
            </SettingsGroup>
          </SettingsHighlightTarget>

          {(appearanceMode.value === "system"
            ? (["light", "dark"] as const)
            : ([appearanceMode.value] as const)
          ).map((half) => (
            // Every control inside edits THIS half, whichever one is
            // painting — that is what `ThemeEditingMode` is for.
            <ThemeEditingMode key={half} mode={half}>
              <ThemeModeSection mode={half} />
            </ThemeEditingMode>
          ))}

          {/* Reset LAST and in its own card. It is an escape hatch from
              everything above, not one more setting among them — a card it
              shared would read it as another thing to configure. */}
          {isAppearanceCustomized ? (
            <SettingsGroup>
              <SettingsGroupRow label="Reset appearance">
                <ThemeResetButton />
              </SettingsGroupRow>
            </SettingsGroup>
          ) : null}

          <SettingsPageGutter />
        </Box>
      </Box>
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/settings/appearance")({
  component: AppearancePage,
});
